// ────────────────────────────────────────────────────────────────────────────
// What the signed-in user may reach, and the one question every gate asks.
//
// ONE SOURCE, SERVER-SIDE. The role's ceiling and the per-user narrowing are
// both computed by the API (`AppCapabilities`) and handed over as a flat list.
// The client does not re-derive them from the role, because the moment it does,
// a preset change on the server stops matching what the screen shows — and the
// screen is the one people believe.
//
// This replaces the five copies of `apps/*/src/auth/currentRole.ts`, which each
// read the same claim and differed only in an app-key constant. Role literals
// scattered through components (`roles={['owner','admin']}`) are what made
// "let a Manager see two Settings pages" a hunt through every file; a
// capability check is a question about the user, asked once.
// ────────────────────────────────────────────────────────────────────────────
import { useQuery } from '@tanstack/react-query';
import { directoryApi, type Me } from '../client';

export const meKeys = {
  all: ['directory', 'me'] as const,
};

/**
 * The caller's own profile. Long-lived: role and capabilities change when an
 * administrator edits them, which is rare, and every gate on every screen
 * reads this — refetching on window focus would be a request per tab switch
 * for data that almost never moves.
 */
export function useMe(options?: { enabled?: boolean }) {
  return useQuery<Me, Error>({
    queryKey: meKeys.all,
    queryFn: () => directoryApi.getMe(),
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    // One retry: a transient failure should not leave a user staring at an
    // empty menu, but hammering an unhealthy API makes it worse.
    retry: 1,
  });
}

export interface Permissions {
  /** True while the answer is still unknown — see `can`. */
  isLoading: boolean;
  /** The profile could not be loaded. Every `can` is then false. */
  error: Error | null;
  /** Platform role, for the few places that genuinely need the rank. */
  role: Me['role'] | null;
  /** Functional roles as stored — what somebody IS, e.g. a Driver. */
  functionalRoles: string[];
  /** The effective capability list from the server. */
  capabilities: string[];
  /**
   * Whether this user may reach a capability.
   *
   * <p>
   * FALSE WHILE LOADING, deliberately. A gate that defaults to "allowed" would
   * flash every menu item and every button on first paint and then take them
   * away, which reads as a bug and, worse, invites a click on something the
   * user cannot do. Callers that need to tell "no" from "not yet" read
   * `isLoading`.
   * </p>
   */
  can: (capability: string) => boolean;
  /** True where the user has any capability in a group, e.g. `settings.`. */
  canAny: (prefix: string) => boolean;
}

export function usePermissions(options?: { enabled?: boolean }): Permissions {
  const query = useMe(options);
  const capabilities = query.data?.capabilities ?? [];
  const granted = new Set(capabilities);

  return {
    isLoading: query.isPending,
    error: query.error ?? null,
    role: query.data?.role ?? null,
    functionalRoles: query.data?.functionalRoles ?? [],
    capabilities,
    can: (capability: string) => granted.has(capability),
    // Used by a parent menu that should appear when any of its children do,
    // rather than needing a capability of its own.
    canAny: (prefix: string) => capabilities.some((c) => c.startsWith(prefix)),
  };
}
